<script lang="ts">
							import SectionComponent from '$lib/section/SectionComponent.svelte';
							import copy from "copy-to-clipboard"; 
							import { onMount } from "svelte";
							import PrevNextSection from '$lib/prevNext/PrevNextSection.svelte';

							interface ComponentProps {
								setVisible: (id: string, v: boolean) => void;
							}
							let { setVisible }: ComponentProps = $props();
							
							const visibleById = $state<Record<string, boolean>>({});

							/**
               * This function will go through all the 'pre' elements
               * on the page and add a copy button to them.
               * Thanks to: https://slavbasharov.com/blog/adding-click-to-copy-code-markdown-blog
               */
              onMount(() => {
                  const codeBlocks = document.querySelectorAll("pre");
                  codeBlocks.forEach((block) => {
                      const copyPrompt = document.createElement("div");
                      copyPrompt.className = "copy-prompt";
                      const copyPromptText = document.createElement("p");
                      copyPromptText.innerHTML = "👆 Click to copy";
                      copyPromptText.className = 'copy-prompt-p';
                      const copyIcon = document.createElement("img");
                      copyIcon.src = "/icons/copy-icon.svg";
                      copyIcon.className = "copy-prompt-img";
                      copyPrompt.appendChild(copyIcon);
                      copyPrompt.appendChild(copyPromptText);
                      block.appendChild(copyPrompt);
											block.querySelector('.copy-prompt > p')?.addEventListener('click', () => {
												if (block.querySelector('code')?.textContent !== null && block.querySelector('code')?.textContent !== undefined) {
													copy(block.querySelector('code')!.textContent!);
												}
												if (block.querySelector('.copy-prompt > p')?.innerHTML !== null && block.querySelector('.copy-prompt > p')?.innerHTML !== undefined) {
													block.querySelector('.copy-prompt > p')!.innerHTML = 'Copied!';
													setTimeout(() => {
														block.querySelector('.copy-prompt > p')!.innerHTML = '👆 Click to copy';
													}, 1000);
												}
											});
                  });
              });
		let prevLink= '/Tutorial/Making_an_Editor';
    let nextLink= '/Tutorial/Expressions_in_Freon';
    
    import Note from "$lib/notes/Note.svelte";
    import Figure from "$lib/figures/Figure.svelte";
</script>

<PrevNextSection {prevLink} {nextLink} />



<SectionComponent tag="h1" 
  id="more-fun-with-projections-1"
  bind:intersecting={visibleById["more-fun-with-projections-1"]}
  onIntersectChange={(visible) => setVisible("more-fun-with-projections-1", visible)}
>More Fun with Projections</SectionComponent>
<p>Freon projections are very flexible. In this lesson we’re going to explore several ways to make your editor look great.
We’re going to create tables!</p>
<SectionComponent tag="h2" 
  id="a-projection-for-the-flow-model-unit-2"
  bind:intersecting={visibleById["a-projection-for-the-flow-model-unit-2"]}
  onIntersectChange={(visible) => setVisible("a-projection-for-the-flow-model-unit-2", visible)}
>A projection for the <em>Flow</em> model unit</SectionComponent>
<p>We almost forgot to make an editor definition for the <em>Flow</em> model unit. Create a file called <code>edu-flow.edit</code>,
and add the following lines. You should be familiar with everything in these projections by now.</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson3-defs/edu-flow.edit

/* This file contains the default editor definition. */

editor default

Flow &#123;[
    Flow $&#123;self.name&#125; for SiteGroup $&#123;self.main&#125;

    $&#123;self.rules vertical&#125;
]&#125;

FlowRule &#123;[
    -------------------------------------
    Name: $&#123;self.name&#125;
    Description: $&#123;self.description&#125;
    From page $&#123;self.page&#125;

        $&#123;self.transitions&#125;
]&#125;

PageTransition &#123;[
    when $&#123;self.condition&#125; goto page $&#123;self.toPage&#125;
]&#125;</code></pre>
<p>Yes—generate the editor and try it! To see this definition in action, click the arrow-left
icon in the top bar and select <strong>StartFlow</strong> from the list of model units. It looks like this:</p>
<Figure
imageName={'tutorial/Tutorial-lesson3-screenshot1.png'}
caption={'Editor after adding projections for the Flow model unit'}
figureNumber={1}
/>
<SectionComponent tag="h2" 
  id="more-than-one-editor-3"
  bind:intersecting={visibleById["more-than-one-editor-3"]}
  onIntersectChange={(visible) => setVisible("more-than-one-editor-3", visible)}
>More than One Editor</SectionComponent>
<p>With Freon you can have multiple views on the same concept. Freon generates a <a
  href="/Background/Projectional_Editing"
>projectional editor</a>.</p>
<p>Remember that the editor definition in the previous lesson and the one above are called <code>default</code>? That’s
because you can create any number of editor definitions, each with its own name.</p>
<p>You can think of an editor as a different view on the model, but in terms of the editor definition,
it’s simply a set of projection definitions. This set can change the appearance of a single concept
or multiple concepts.</p>
<p>When an editor definition doesn’t include a projection for a certain concept, it falls back to a projection from another
editor definition. Which projection is chosen is determined by a fixed algorithm.
For details, see <a
  href="/Documentation/Defining_an_Editor#editor-precedence-4"
>Editor Precedence</a>.</p>
<Note> {#snippet header()} One property, one occurrence in the editor{/snippet}
{#snippet content()}
<p>Unfortunately, it’s not possible to show the same property of a concept twice in the same editor.
The tooling that keeps the underlying model and the editor view in sync doesn’t allow this.</p>
{/snippet}
</Note>
<SectionComponent tag="h2" 
  id="creating-tables-4"
  bind:intersecting={visibleById["creating-tables-4"]}
  onIntersectChange={(visible) => setVisible("creating-tables-4", visible)}
>Creating Tables</SectionComponent>
<p>Let’s create a second editor definition for the <em>Flow</em> model unit—one that displays
the list of <em>PageTransitions</em> in a <em>FlowRule</em> as a table.
We’ll name this editor definition <code>rules_as_table</code>. Put it in <code>edu-flow-table.edit</code>.</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson3-defs/edu-flow-table.edit#L3-L3

editor rules_as_table</code></pre>
<p>To create a projection for <em>FlowRule</em>, we’ll reuse the default projection with one change:
we remove the keyword <code>vertical</code> from <code>$&#123;self.transitions&#125;</code> and add <code>table</code>.</p>
<p>Furthermore, we want one element per row, so we add the keyword <code>rows</code>.<br>
(You can also display one element per column using <code>columns</code>.)</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson3-defs/edu-flow-table.edit#L5-L12

FlowRule &#123;[
    -------------------------------------
    Name: $&#123;name&#125;
        Description: $&#123;self.description&#125;
        For page $&#123;page&#125;

        $&#123;self.transitions table rows&#125;
]&#125;</code></pre>
<p>Next, define the table itself. Each element in the list is an instance of the <em>PageTransition</em> concept,
so we add a projection for this concept—but we prefix it with the keyword <code>table</code>.<br>
This means the projection is only used when the concept is rendered within a list projected <strong>as a table</strong>.</p>
<p>You may also add headers (taken literally). Here the headers are <code>Condition</code> and <code>Goto Page</code>.<br>
The <code>|</code> character separates columns. Headers are optional.</p>
<p>Finally, specify the cell content by indicating which property goes into each column.</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson3-defs/edu-flow-table.edit#L14-L17

PageTransition &#123; table [
    Condition          | Goto Page
    $&#123;self.condition&#125;  |  $&#123;self.toPage&#125;
]&#125;</code></pre>
<p>Note that for <em>PageTransition</em> we defined a table with two parts. The <strong>context</strong> in which these objects are projected
determines whether they appear as <strong>rows</strong> or <strong>columns</strong>.</p>
<p>After saving <code>edu-flow-table.edit</code>, run the generator again. Find the model unit
named <strong>StartFlow</strong> and… yes, there’s a table. Try switching <code>rows</code> to <code>columns</code> in the <em>FlowRule</em> projection
to see the difference.</p>
<Figure
imageName={'tutorial/Tutorial-lesson3-screenshot2.png'}
caption={'Editor after adding projections for the Flow model unit'}
figureNumber={2}
/>
<p>If you want to revert to the earlier look, open the <strong>View</strong> menu, deselect <strong>rules_as_table</strong>, and click <strong>Apply changes</strong>.<br>
The menu lists each editor definition by name; as you add more, you can toggle them on or off.</p>
<SectionComponent tag="h2" 
  id="using-a-specific-editor-5"
  bind:intersecting={visibleById["using-a-specific-editor-5"]}
  onIntersectChange={(visible) => setVisible("using-a-specific-editor-5", visible)}
>Using a Specific Editor</SectionComponent>
<p>We mentioned that there is an <a
  href="/Documentation/Defining_an_Editor#editor-precedence-4"
>algorithm</a> that determines which projection is shown.
Sometimes you want to force a specific projection. You can do this by stating the concept <strong>and</strong> the editor name using
<code>[=&gt;ConceptName:editor-name]</code>—for example, <code>[=&gt;Page:footing]</code>.</p>
<p>Before using the <code>footing</code> editor, create it. Add a file called <code>page-footing.edit</code> and copy:</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson3-defs/page-footing.edit#L3-L8

editor footing

Page &#123;[
    Questions:
        $&#123;self.questions vertical terminator [END]&#125;
]&#125;</code></pre>
<p>Now we can specify a specific editor. Rewrite <code>edu-topics.edit</code> as follows:</p>
<pre class="language-freon"><code class="language-freon">// Education/lesson3-defs/edu-topics.edit#L17-L60


Theory &#123;[
    ----------------------------------------------------
    Theory [=&gt;Page]
        $&#123;self.content vertical  terminator[== END OF LINE]&#125;

        [=&gt;Page:footing]
]&#125;

Video &#123;[
    ----------------------------------------------------
    Video [=&gt;Page]
        Maybe this video will help you understand.
        $&#123;self.url&#125;

        [=&gt;Page:footing]
]&#125;

WorkSheet &#123;[
    ----------------------------------------------------
    Worksheet [=&gt;Page]
        See if you can answer the following questions.

        [=&gt;Page:footing]
]&#125;

ExamplePage &#123;[
    ----------------------------------------------------
    Example [=&gt;Page]
        $&#123;self.content&#125;

        Now, please, answer the following questions.

        [=&gt;Page:footing]
]&#125;

InDepthMaterial &#123;[
    ----------------------------------------------------
    InDepthMaterial [=&gt;Page]
        $&#123;self.content&#125;

        Test your understanding by answering the following questions.

        [=&gt;Page:footing]</code></pre>
<p>Try it out! There’s a lot of fun to be had with projections.
In the next lesson we’ll add expressions to the metamodel and to the editor definitions.</p>


<PrevNextSection {prevLink} {nextLink} />